import { App } from '@capacitor/app';
import { differenceInMinutes, minutesToMilliseconds } from 'date-fns';
import { isUndefined, noop } from 'lodash-es';
import { log } from '../log';
import type { TokenPayload } from './authentication-tokens';
import { getEarliestExpirationDate } from './token-utils';

export async function setupAutomaticTokenRefresh(
  getTokens: () => (TokenPayload | null)[],
  triggerTokenRefresh: () => Promise<void>,
): Promise<() => Promise<void>> {

  let timeout: ReturnType<typeof setTimeout>;
  let lastTokenRefreshTime: Date;

  const refreshToken = async () => {
    try {
      lastTokenRefreshTime = new Date();
      await triggerTokenRefresh();
    } catch (error) {
      log.error('Automatic token refresh failed', error);
    }
  };

  const appListener = await App.addListener('appStateChange', async appState => {
    if (appState.isActive) {
      log.debug('App state changed to ACTIVE. Refreshing token...');
      await refreshToken();
    }
  });

  const setTokenRefreshTimeout = () => {
    const expirationDate = getEarliestExpirationDate(getTokens());

    if (isUndefined(expirationDate)) {
      log.warn('No tokens found for automatic token refresh.');
      return noop;
    }

    const minutesUntilExpiration = differenceInMinutes(
      expirationDate,
      new Date(),
    );

    const minutesSinceLastTokenRefresh = differenceInMinutes(
      lastTokenRefreshTime,
      new Date(),
    );

    const oneMinuteEarlier = minutesUntilExpiration - 1;
    let minutesUntilTokenRefresh = Math.max(0, oneMinuteEarlier);

    if (minutesSinceLastTokenRefresh < 1) {
      log.debug('Less than 1 minute has passed since last token refresh. Ignoring expiration time.');
      minutesUntilTokenRefresh = 2;
    }

    log.debug(`Tokens will be refeshed automatically in ${minutesUntilTokenRefresh} minutes.`);


    timeout = setTimeout(
      async () => {
        await refreshToken();
        setTokenRefreshTimeout();
      },
      minutesToMilliseconds(minutesUntilTokenRefresh),
    );
  };

  setTokenRefreshTimeout();

  return async () => {
    clearTimeout(timeout);
    await appListener.remove();
  };
}
